/**
 * CrFileReviewModal — Schedule / Complete File Review modal
 *
 * Two modes:
 *  - Schedule mode: create a new review (reviewType + scheduledDate required)
 *  - Complete mode: update review status to completed (reviewOutcome required)
 *
 * Schema facts (from compliance-cr.ts route):
 *  - reviewType: "spot_check" | "random" | "targeted" | "complaint_triggered" | "annual"
 *  - reviewStatus: "scheduled" | "in_progress" | "completed" | "cancelled"
 *  - reviewOutcome not in schema — only reviewStatus + findings/actionsRequired on PATCH
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FileReviewData {
  id?: string;
  reviewType?: string | null;
  scheduledDate?: string | null;
  reviewStatus?: string | null;
  reviewOutcome?: string | null;
  findings?: string | null;
  actionsRequired?: string | null;
  dealId?: string | null;
  reviewerId?: string | null;
}

interface CrFileReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditRepId: string;
  /** When provided, modal is in "complete review" mode */
  editingReview?: FileReviewData | null;
  /** When true, modal is in schedule mode (create new review) */
  scheduleMode?: boolean;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REVIEW_TYPES = [
  { value: "spot_check", label: "Spot Check" },
  { value: "random", label: "Random" },
  { value: "targeted", label: "Targeted" },
  { value: "complaint_triggered", label: "Complaint Triggered" },
  { value: "annual", label: "Annual" },
];

const REVIEW_STATUSES = [
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const REVIEW_OUTCOMES = [
  { value: "satisfactory", label: "Satisfactory" },
  { value: "needs_improvement", label: "Needs Improvement" },
  { value: "unsatisfactory", label: "Unsatisfactory" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CrFileReviewModal({
  open,
  onOpenChange,
  creditRepId,
  editingReview,
  scheduleMode = false,
  onSuccess,
}: CrFileReviewModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingReview?.id;
  const isCompleteMode = isEditMode && !scheduleMode;

  // Form state
  const [reviewType, setReviewType] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [reviewStatus, setReviewStatus] = useState("scheduled");
  const [reviewOutcome, setReviewOutcome] = useState("none");
  const [findings, setFindings] = useState("");
  const [actionsRequired, setActionsRequired] = useState("");

  // Reset / pre-populate form when modal opens
  useEffect(() => {
    if (open && editingReview) {
      setReviewType(editingReview.reviewType ?? "");
      setScheduledDate(editingReview.scheduledDate ?? "");
      setReviewStatus(editingReview.reviewStatus ?? "scheduled");
      setReviewOutcome(editingReview.reviewOutcome ?? "none");
      setFindings(editingReview.findings ?? "");
      setActionsRequired(editingReview.actionsRequired ?? "");
    } else if (open && !editingReview) {
      setReviewType("");
      setScheduledDate("");
      setReviewStatus("scheduled");
      setReviewOutcome("none");
      setFindings("");
      setActionsRequired("");
    }
  }, [open, editingReview]);

  // Form validity
  const isScheduleValid = reviewType.length > 0 && scheduledDate.length > 0;
  const isCompleteValid = reviewStatus === "completed"
    ? reviewOutcome !== "none"
    : true;
  const isValid = isEditMode ? isCompleteValid : isScheduleValid;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/credit-reps/${creditRepId}/file-reviews`,
        {
          reviewType,
          scheduledDate,
          reviewStatus: "scheduled",
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to schedule file review");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}/file-reviews`],
      });
      toast({ title: "File review scheduled successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingReview?.id) throw new Error("No review ID");
      const body: Record<string, string | undefined> = {
        reviewStatus,
      };
      if (reviewOutcome !== "none") {
        body.reviewOutcome = reviewOutcome;
      }
      if (findings.trim()) {
        body.findings = findings.trim();
      }
      if (actionsRequired.trim()) {
        body.actionsRequired = actionsRequired.trim();
      }
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/file-reviews/${editingReview.id}`,
        body
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to update file review");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}/file-reviews`],
      });
      toast({ title: "File review updated successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const title = isCompleteMode
    ? "Complete File Review"
    : scheduleMode
    ? "Schedule File Review"
    : "File Review";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="modal-cr-file-review-form"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isCompleteMode
              ? "Record the outcome of this file review."
              : "Schedule a new file review for this credit representative."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Schedule Mode Fields */}
          {!isCompleteMode && (
            <>
              <div className="space-y-1.5">
                <Label>
                  Review Type <span className="text-destructive">*</span>
                </Label>
                <Select value={reviewType} onValueChange={setReviewType}>
                  <SelectTrigger data-testid="select-review-type">
                    <SelectValue placeholder="Select review type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="scheduled-date">
                  Scheduled Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="scheduled-date"
                  data-testid="input-scheduled-date"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Complete Mode Fields */}
          {isCompleteMode && (
            <>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={reviewStatus} onValueChange={setReviewStatus}>
                  <SelectTrigger data-testid="select-review-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>
                  Outcome{reviewStatus === "completed" && <span className="text-destructive"> *</span>}
                </Label>
                <Select value={reviewOutcome} onValueChange={setReviewOutcome}>
                  <SelectTrigger data-testid="select-review-outcome">
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Set</SelectItem>
                    {REVIEW_OUTCOMES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="findings">Findings</Label>
                <Textarea
                  id="findings"
                  data-testid="input-findings"
                  value={findings}
                  onChange={(e) => setFindings(e.target.value)}
                  placeholder="Summary of review findings..."
                  rows={3}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="actions-required">Actions Required</Label>
                <Textarea
                  id="actions-required"
                  data-testid="input-actions-required"
                  value={actionsRequired}
                  onChange={(e) => setActionsRequired(e.target.value)}
                  placeholder="Actions required as a result of the review..."
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isCompleteMode
              ? "Save Changes"
              : "Schedule Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
